import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getIepChecklist, updateIepChecklistSection, exportIepChecklistPdf, deleteIepChecklist, evaluateIepFromDocs } from '../../services/iepChecklistService';
import { getStudent } from '../../services/studentService';
import { getFileView } from '../../services/fileCabinetService';
import ConfirmModal from '../common/ConfirmModal';
import Loader from '../common/Loader';
import Alert from '../common/Alert';
import DocumentViewer from '../common/DocumentViewer';
import DatePicker from '../common/DatePicker';
import ChecklistSectionPopover from './ChecklistSectionPopover';
import {
  formatChecklistDate,
  formatChecklistDateList,
  formatKey,
} from '../../utils/checklistFormatting';

const B = 'border border-slate-300 dark:border-slate-600';
const CELL = `${B} px-3 py-2 text-sm text-gray-900 dark:text-slate-200 align-middle`;
const HCELL = `${B} px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700`;
const SECTION_TITLE = `${B} px-3 py-2.5 text-sm font-semibold text-indigo-800 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30`;

function CB({ checked }) {
  if (checked) return <span className="text-green-600 dark:text-green-400 font-medium">☑</span>;
  return <span className="text-slate-400 dark:text-slate-500">☐</span>;
}

function CBLabel({ label, checked, editing, onChange }) {
  if (editing) {
    return (
      <label className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
        <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="w-3.5 h-3.5 rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500" />
        <span className="text-sm text-gray-700 dark:text-slate-300">{label}</span>
      </label>
    );
  }
  return <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><CB checked={checked} /> <span className="text-sm">{label}</span></span>;
}

function YesNoInline({ obj, editing, onChange }) {
  if (!obj || typeof obj !== 'object') return <span className="text-xs text-slate-400">—</span>;
  const keys = Object.keys(obj);
  if (editing) {
    return (
      <span className="inline-flex items-center gap-4">
        {keys.map(k => (
          <label key={k} className="inline-flex items-center gap-1 cursor-pointer whitespace-nowrap">
            <input type="radio" name={JSON.stringify(obj) + k} checked={!!obj[k]} onChange={() => { const u = {}; keys.forEach(kk => { u[kk] = kk === k; }); onChange(u); }} className="text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5" />
            <span className="text-sm text-gray-700 dark:text-slate-300">{formatKey(k)}</span>
          </label>
        ))}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-3">
      {keys.map(k => (
        <span key={k} className="inline-flex items-center gap-1 whitespace-nowrap">
          <CB checked={obj[k]} /> <span className="text-sm">{formatKey(k)}</span>
        </span>
      ))}
    </span>
  );
}

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

export default function IepComplianceChecklist() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [goalProgress, setGoalProgress] = useState([]);
  const [showStickyScore, setShowStickyScore] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const [viewerPage, setViewerPage] = useState(null);
  const timelineSaveRef = useRef(null);
  const goalProgressSaveRef = useRef(null);
  const progressBarRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [studentRes, checklistRes] = await Promise.allSettled([
        getStudent(id),
        getIepChecklist(id),
      ]);
      if (studentRes.status === 'fulfilled') setStudent(studentRes.value.data.data);
      if (checklistRes.status === 'fulfilled') {
        const ckData = checklistRes.value.data.data;
        setChecklist(ckData);
        setGoalProgress(ckData?.goal_progress || []);
        setNoData(false);
      } else {
        const status = checklistRes.reason?.response?.status;
        if (status === 404 || status === 422 || !checklistRes.reason?.response) {
          setNoData(true);
        } else {
          setAlert({ type: 'error', message: checklistRes.reason?.response?.data?.detail || 'Failed to load IEP checklist.' });
        }
      }
    } catch {
      setAlert({ type: 'error', message: 'Failed to load data.' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const target = checklist?.completion_score ?? 0;
    setAnimatedScore(0);
    if (!target) return;
    const duration = 1000;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [checklist?.completion_score]);

  useEffect(() => {
    const el = progressBarRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyScore(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [checklist]);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const response = await exportIepChecklistPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `iep_checklist_student_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to export PDF.' });
    } finally {
      setExporting(false);
    }
  };

  const handleScanDocuments = async () => {
    setScanning(true);
    setAlert(null);
    try {
      const response = await evaluateIepFromDocs(id);
      const msg = response.data?.message || 'Documents scanned successfully.';
      setChecklist(response.data?.data || checklist);
      setAlert({ type: 'success', message: msg });
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setAlert({ type: 'error', message: 'Upload documents first before scanning.' });
      } else {
        setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to scan documents.' });
      }
    } finally {
      setScanning(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteIepChecklist(id);
      setShowDeleteModal(false);
      setChecklist(null);
      setNoData(true);
      setEditing(false);
      setEditData(null);
      setAlert({ type: 'success', message: 'Checklist data cleared successfully.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to delete checklist.' });
    } finally {
      setDeleting(false);
    }
  };

  const startEditing = () => { setEditData(deepClone(checklist)); setEditing(true); };
  const cancelEditing = () => { setEditing(false); setEditData(null); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sections = ['header', 'timeline_items', 'pre_meeting_check', 'post_meeting', 'transition_plans'];
      for (const section of sections) {
        if (editData[section] && JSON.stringify(editData[section]) !== JSON.stringify(checklist[section])) {
          await updateIepChecklistSection(id, section, editData[section]);
        }
      }
      await fetchData();
      setEditing(false);
      setEditData(null);
      setAlert({ type: 'success', message: 'Checklist updated successfully.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to save changes.' });
    } finally {
      setSaving(false);
    }
  };

  const updateEditField = (sectionPath, value) => {
    setEditData(prev => {
      const updated = deepClone(prev);
      const keys = sectionPath.split('.');
      let ref = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        if (ref[keys[i]] === undefined || ref[keys[i]] === null) ref[keys[i]] = {};
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const updateTimelineItem = (idx, field, value) => {
    setChecklist(prev => {
      const items = [...(prev.timeline_items || [])];
      items[idx] = { ...items[idx], [field]: value };
      const updated = { ...prev, timeline_items: items };
      if (timelineSaveRef.current) clearTimeout(timelineSaveRef.current);
      timelineSaveRef.current = setTimeout(() => {
        updateIepChecklistSection(id, 'timeline_items', items).catch(err => {
          setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to save timeline.' });
        });
      }, 500);
      return updated;
    });
  };

  const updateGoalProgressField = (idx, field, value) => {
    setGoalProgress(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], [field]: value };
      if (goalProgressSaveRef.current) clearTimeout(goalProgressSaveRef.current);
      goalProgressSaveRef.current = setTimeout(() => {
        updateIepChecklistSection(id, 'goal_progress', items).catch(err => {
          setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to save goal progress.' });
        });
      }, 500);
      return items;
    });
  };

  const handleOpenFileAtPage = async (fileId, page, fileName) => {
    try {
      const { data } = await getFileView(id, fileId, page);
      const fileData = data.data || data;
      setViewerFile({
        file_name: fileData.file_name || fileName || 'Document',
        mime_type: fileData.mime_type || 'application/pdf',
        file_type: 'IEP',
        student_id: id,
        id: fileData.student_file_id || fileId,
        student_file_id: fileData.student_file_id || fileId,
      });
      setViewerPage(fileData.page || page || null);
    } catch {
      setAlert({ type: 'error', message: 'Could not load file for viewing.' });
    }
  };

  if (loading) return <Loader fullScreen text="Loading IEP checklist..." />;

  const data = editing ? editData : checklist;
  const header = data?.header;
  const timeline = data?.timeline_items || [];
  const pre = data?.pre_meeting_check || {};
  const post = data?.post_meeting || {};
  const transition = data?.transition_plans || {};
  const sd = data?.section_details || {};
  const score = data?.completion_score ?? 0;
  const scoreColor = score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const scoreTrack = 'bg-slate-200 dark:bg-slate-700';

  return (
    <div>
      {alert && (
        <div className="fixed top-20 right-4 z-50 w-80 sm:w-96">
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      <ConfirmModal
        open={showDeleteModal}
        title="Clear Checklist Data"
        message="This will clear all checklist data for this student. The checklist can be re-populated by running compliance again."
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        confirmText="Delete"
        variant="danger"
        loading={deleting}
      />

      {/* Page header */}
      <div className="px-3 sm:px-6 pt-3 sm:pt-6 mb-4 sm:mb-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={() => navigate(`/students/${id}/information`)} className="p-1.5 sm:p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white truncate">IEP Compliance Checklist</h1>
              {student && <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400 truncate">{student.student_name}</p>}
            </div>
          </div>
          {checklist && !noData && (
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 flex-wrap justify-end">
              <button onClick={handleScanDocuments} disabled={scanning} title="Scan Documents with AI" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50">
                {scanning ? (
                  <><svg className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg><span className="hidden md:inline">Scanning...</span></>
                ) : (
                  <><svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg><span className="hidden md:inline">Scan Docs</span></>
                )}
              </button>
              <button onClick={handleExportPdf} disabled={exporting} title="Export PDF" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span className="hidden md:inline">{exporting ? 'Exporting...' : 'Export PDF'}</span>
              </button>
              {editing && (
                <button onClick={cancelEditing} disabled={saving} title="Cancel editing" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-gray-500 text-white hover:bg-gray-600 transition-colors disabled:opacity-50">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="hidden md:inline">Cancel</span>
                </button>
              )}
              <button onClick={editing ? handleSave : startEditing} disabled={editing && saving} title={editing ? 'Save' : 'Edit'} className={`inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-colors disabled:opacity-50 ${editing ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-amber-500 text-white hover:bg-amber-600'}`}>
                {editing ? (
                  saving ? (
                    <svg className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  )
                ) : (
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                )}
                <span className="hidden md:inline">{editing ? (saving ? 'Saving...' : 'Save') : 'Edit'}</span>
              </button>
              <button onClick={() => setShowDeleteModal(true)} title="Delete" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                <span className="hidden md:inline">Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sticky score badge */}
      {showStickyScore && checklist && (
        <div className="fixed top-14 sm:top-16 right-3 sm:right-6 z-50 flex items-center gap-2 sm:gap-2.5 bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 shadow-lg transition-all">
          <div className="w-16 sm:w-24 h-1.5 sm:h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${scoreColor}`} style={{ width: `${animatedScore}%` }} />
          </div>
          <span className="text-[10px] sm:text-xs font-bold text-gray-700 dark:text-slate-200">{animatedScore}%</span>
        </div>
      )}

      {/* Content */}
      <div className="px-3 sm:px-6 pb-6">
        {/* Empty State */}
        {noData && !checklist && (
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-6 sm:p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">No IEP Compliance Checklist found for this student yet.</h3>
            <p className="text-gray-500 dark:text-slate-400 text-sm mt-2 max-w-md mx-auto">The checklist will be automatically created when compliance evaluation runs.</p>
            <button onClick={() => navigate(`/students/${id}/information`)} className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors shadow-sm">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              Go to Compliance Report
            </button>
          </div>
        )}

        {/* === CHECKLIST DOCUMENT === */}
        {checklist && (
          <div className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-none sm:rounded-lg shadow-sm overflow-x-auto -mx-3 sm:mx-0">
           <div className="min-w-0 sm:min-w-[560px]">

            {/* ─── SECTION A: HEADER — stacks on mobile ─── */}
            <div className="grid grid-cols-1 sm:grid-cols-2">
              <div className={`${CELL} font-medium`}>
                <span className="text-xs text-slate-500 dark:text-slate-400">Student Name</span>
                <br />
                <span className="text-sm sm:text-base font-semibold">{header?.student_name || student?.student_name || '—'}</span>
              </div>
              <div className={`${CELL}`}>
                <span className="text-xs text-slate-500 dark:text-slate-400">IEP Due</span>
                <br />
                {editing ? (
                  <DatePicker
                    value={editData?.header?.iep_due || ''}
                    onChange={(v) => updateEditField('header.iep_due', v)}
                    className="px-2 py-0.5 rounded border border-gray-200 dark:border-[#334155] bg-transparent dark:bg-[#1e293b] text-gray-900 dark:text-white text-sm mt-0.5 w-full sm:w-auto focus:ring-1 focus:ring-indigo-500"
                    aria-label="IEP Due"
                  />
                ) : (
                  <>
                    <span className="font-medium">{formatChecklistDate(header?.iep_due)}</span>
                    {formatChecklistDateList(header?.iep_due) && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {formatChecklistDateList(header?.iep_due).slice(1).map((d, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] text-slate-500 dark:text-slate-400">{d}</span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div ref={progressBarRef} className="grid grid-cols-1 sm:grid-cols-2">
              <div className={CELL}>
                <span className="text-xs text-slate-500 dark:text-slate-400">Status</span>
                <div className="flex items-center gap-2 sm:gap-3 mt-1">
                  <span className={`inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold shrink-0 ${data?.status === 'completed' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    <span className={`w-2 h-2 rounded-full ${data?.status === 'completed' ? 'bg-green-500' : 'bg-amber-500'}`} />
                    {data?.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                  <div className={`flex-1 h-4 sm:h-5 rounded-full ${scoreTrack} overflow-hidden relative min-w-[80px] sm:min-w-[120px]`}>
                    <div className={`h-full rounded-full ${scoreColor} flex items-center justify-end pr-1.5 sm:pr-2`} style={{ width: `${Math.max(animatedScore, 12)}%`, transition: 'none' }}>
                      <span className="text-[10px] sm:text-[11px] font-bold text-white drop-shadow-sm whitespace-nowrap">{animatedScore}%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={CELL}>
                <span className="text-xs text-slate-500 dark:text-slate-400">IEP Scheduled</span>
                <br />
                {editing ? (
                  <DatePicker
                    value={editData?.header?.iep_scheduled || ''}
                    onChange={(v) => updateEditField('header.iep_scheduled', v)}
                    className="px-2 py-0.5 rounded border border-gray-200 dark:border-[#334155] bg-transparent dark:bg-[#1e293b] text-gray-900 dark:text-white text-sm mt-0.5 w-full sm:w-auto focus:ring-1 focus:ring-indigo-500"
                    aria-label="IEP Scheduled"
                  />
                ) : (
                  <>
                    <span className="font-medium">{formatChecklistDate(header?.iep_scheduled)}</span>
                    {formatChecklistDateList(header?.iep_scheduled) && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {formatChecklistDateList(header?.iep_scheduled).slice(1).map((d, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] text-slate-500 dark:text-slate-400">{d}</span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ─── SECTION B: TIMELINE ─── */}
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <td colSpan={4} className={SECTION_TITLE}> TIMELINE</td>
                </tr>
                <tr>
                  <th className={`${HCELL} w-6 sm:w-10 text-center`}>#</th>
                  <th className={HCELL}>Task</th>
                  <th className={`${HCELL} w-24 sm:w-32`}>Due Date</th>
                  <th className={`${HCELL} w-16 sm:w-28 text-center`}><span className="hidden sm:inline">Sent / </span>Complete</th>
                </tr>
              </thead>
              <tbody>
                {timeline.length === 0 ? (
                  <tr><td colSpan={4} className={`${CELL} text-center text-slate-400 dark:text-slate-500 italic`}>No timeline items.</td></tr>
                ) : timeline.map((item, idx) => (
                  <tr key={idx} className={item.sent_complete === true ? 'bg-green-50/60 dark:bg-green-900/10' : 'bg-amber-50/40 dark:bg-amber-900/10'}>
                    <td className={`${CELL} text-center text-xs font-medium text-slate-500`}>{idx + 1}</td>
                    <td className={CELL}>
                      {editing ? (
                        <input type="text" value={editData?.timeline_items?.[idx]?.task || ''} onChange={e => { const items = [...(editData.timeline_items || [])]; items[idx] = { ...items[idx], task: e.target.value }; updateEditField('timeline_items', items); }} className="w-full px-2 py-0.5 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm" />
                      ) : <span className="text-sm">{item.task}</span>}
                    </td>
                    <td className={CELL}>
                      <DatePicker
                        value={item.due_date || ''}
                        onChange={(v) => updateTimelineItem(idx, 'due_date', v || null)}
                        className="w-full px-1.5 py-0.5 text-xs rounded border border-gray-200 dark:border-[#334155] bg-transparent dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-1 focus:ring-indigo-500"
                        aria-label="Due date"
                      />
                    </td>
                    <td className={`${CELL} text-center`}>
                      <input
                        type="checkbox"
                        checked={item.sent_complete === true}
                        onChange={e => updateTimelineItem(idx, 'sent_complete', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-green-600 focus:ring-green-500 cursor-pointer"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ─── SECTION C: PRE-MEETING COMPLIANCE CHECK ─── */}
            <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[480px]">
              <tbody>
                <tr><td colSpan={6} className={SECTION_TITLE}>COMPLIANCE PRE-MEETING CHECK</td></tr>

                {/* c1: Date of IEP */}
                <tr>
                  <td colSpan={3} className={CELL}>
                    <ChecklistSectionPopover sectionKey="identity_timeline" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Date of IEP:</span>
                    </ChecklistSectionPopover>
                    {editing ? (
                      <div className="inline-block ml-2 align-middle min-w-[9rem]">
                        <DatePicker
                          value={editData?.pre_meeting_check?.date_of_iep || ''}
                          onChange={(v) => updateEditField('pre_meeting_check.date_of_iep', v)}
                          className="px-2 py-0.5 rounded border border-gray-200 dark:border-[#334155] bg-transparent dark:bg-[#1e293b] text-gray-900 dark:text-white text-sm focus:ring-1 focus:ring-indigo-500"
                          aria-label="Date of IEP"
                        />
                      </div>
                    ) : (
                      <span className="ml-2">{formatChecklistDate(pre.date_of_iep)}</span>
                    )}
                  </td>
                  <td colSpan={3} className={CELL} />
                </tr>
                <tr>
                  <td colSpan={3} className={CELL}>Has it been a year since previous IEP?</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={pre.year_since_previous_iep} editing={editing} onChange={v => updateEditField('pre_meeting_check.year_since_previous_iep', v)} /></td>
                </tr>
                {(pre.explanation || editing) && (
                  <tr>
                    <td colSpan={6} className={CELL}>
                      <span className="text-xs text-slate-500">Explanation: </span>
                      {editing ? (
                        <input type="text" value={editData?.pre_meeting_check?.explanation || ''} onChange={e => updateEditField('pre_meeting_check.explanation', e.target.value)} className="ml-1 flex-1 px-2 py-0.5 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm w-3/4" />
                      ) : (
                        <span className="italic text-slate-500 dark:text-slate-400">{pre.explanation || '—'}</span>
                      )}
                    </td>
                  </tr>
                )}

                {/* c2: Notice of Meeting */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="iep_compliance" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Notice of Meeting includes:</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                <tr>
                  {pre.notice_of_meeting && Object.entries(pre.notice_of_meeting).map(([k, v]) => (
                    <td key={k} className={CELL}>
                      <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`pre_meeting_check.notice_of_meeting.${k}`, val)} />
                    </td>
                  ))}
                  {!pre.notice_of_meeting && <td colSpan={6} className={`${CELL} text-slate-400 italic`}>No data</td>}
                </tr>

                {/* c3: PLAAFP */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="present_levels" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">PLAAFP:</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                {pre.plaffp && (
                  <tr>
                    {Object.entries(pre.plaffp).map(([k, v]) => (
                      <td key={k} className={CELL} colSpan={k === 'ties_goals' ? 3 : undefined}>
                        <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`pre_meeting_check.plaffp.${k}`, val)} />
                      </td>
                    ))}
                  </tr>
                )}

                {/* c4: Impact Statements */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="present_levels" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Impact Statements:</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} className={CELL}><YesNoInline obj={pre.impact_statements} editing={editing} onChange={v => updateEditField('pre_meeting_check.impact_statements', v)} /></td>
                  <td colSpan={4} className={CELL} />
                </tr>

                {/* c5: Special Factors */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKeys={['eligibility', 'evaluation']} sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Special Factors Noted:</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                {pre.special_factors?.noted && (
                  <tr>
                    {Object.entries(pre.special_factors.noted).map(([k, v]) => (
                      <td key={k} className={CELL}>
                        <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`pre_meeting_check.special_factors.noted.${k}`, val)} />
                      </td>
                    ))}
                    {Object.keys(pre.special_factors.noted).length < 6 && <td className={CELL} colSpan={6 - Object.keys(pre.special_factors.noted).length} />}
                  </tr>
                )}
                <tr><td colSpan={6} className={`${CELL} bg-slate-50/60 dark:bg-slate-800/40 font-medium text-xs`}>Addressed In:</td></tr>
                {pre.special_factors?.addressed_in && (
                  <tr>
                    {Object.entries(pre.special_factors.addressed_in).map(([k, v]) => (
                      <td key={k} className={CELL} colSpan={2}>
                        <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`pre_meeting_check.special_factors.addressed_in.${k}`, val)} />
                      </td>
                    ))}
                  </tr>
                )}
                <tr>
                  <td colSpan={3} className={CELL}>Considered not needed but addressed?</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={pre.special_factors?.considered_not_needed_addressed} editing={editing} onChange={v => updateEditField('pre_meeting_check.special_factors.considered_not_needed_addressed', v)} /></td>
                </tr>

                {/* c6: Goals */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="goals" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Goals</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                <tr>
                  <td colSpan={3} className={CELL}>Goals address areas of need:</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={pre.goals_address_needs} editing={editing} onChange={v => updateEditField('pre_meeting_check.goals_address_needs', v)} /></td>
                </tr>
                <tr>
                  <td className={`${CELL} font-medium text-xs`}>
                    <ChecklistSectionPopover sectionKey="evaluation" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span>Evaluation:</span>
                    </ChecklistSectionPopover>
                  </td>
                  {pre.goals_evaluation && Object.entries(pre.goals_evaluation).map(([k, v]) => (
                    <td key={k} className={CELL}>
                      <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`pre_meeting_check.goals_evaluation.${k}`, val)} />
                    </td>
                  ))}
                  {pre.goals_evaluation && Object.keys(pre.goals_evaluation).length < 5 && <td className={CELL} colSpan={5 - Object.keys(pre.goals_evaluation).length} />}
                </tr>
                <tr>
                  <td colSpan={3} className={CELL}>Descriptive benchmarks:</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={pre.benchmarks} editing={editing} onChange={v => updateEditField('pre_meeting_check.benchmarks', v)} /></td>
                </tr>

                {/* c7: Measurement & Progress */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="goals" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Measurement &amp; Progress</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                <tr>
                  <td colSpan={3} className={CELL}>How goal will be measured:</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={pre.measurement} editing={editing} onChange={v => updateEditField('pre_meeting_check.measurement', v)} /></td>
                </tr>
                <tr>
                  <td colSpan={3} className={CELL}>Progress Reporting:</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={pre.progress_reporting} editing={editing} onChange={v => updateEditField('pre_meeting_check.progress_reporting', v)} /></td>
                </tr>

                {/* c7b: Goal Details & Progress */}
                {goalProgress.length > 0 && (
                  <>
                    <tr>
                      <td colSpan={6} className={`${B} px-3 py-2.5 text-sm font-semibold text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20`}>
                        GOAL DETAILS &amp; PROGRESS
                      </td>
                    </tr>
                    {goalProgress.map((goal, gi) => (
                      <tr key={gi}>
                        <td colSpan={6} className={`${CELL} !p-0`}>
                          <div className={`p-3 ${gi > 0 ? 'border-t border-slate-200 dark:border-slate-700' : ''}`}>
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                Goal {gi + 1}: {goal.goal_area || 'Untitled'}
                              </span>
                              {goal.is_measurable && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                  <span>✓</span> Measurable
                                </span>
                              )}
                            </div>
                            {goal.description && (
                              <p className="text-xs text-slate-600 dark:text-slate-400 mb-2.5 leading-relaxed">{goal.description}</p>
                            )}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2.5">
                              <div><span className="text-slate-500 dark:text-slate-400">Baseline:</span> <span className="font-medium text-gray-800 dark:text-slate-200">{goal.baseline || '—'}</span></div>
                              <div><span className="text-slate-500 dark:text-slate-400">Target:</span> <span className="font-medium text-gray-800 dark:text-slate-200">{goal.target || '—'}</span></div>
                              <div><span className="text-slate-500 dark:text-slate-400">Method:</span> <span className="text-gray-700 dark:text-slate-300">{goal.measurement_method || '—'}</span></div>
                              <div><span className="text-slate-500 dark:text-slate-400">Frequency:</span> <span className="text-gray-700 dark:text-slate-300">{goal.monitoring_frequency || '—'}</span></div>
                            </div>
                            <div className="border-t border-slate-100 dark:border-slate-700 pt-2 space-y-1.5">
                              <div className="flex items-center gap-1.5 text-xs">
                                <span className="text-slate-500 dark:text-slate-400">▸ Current Progress:</span>
                                <span className={`font-semibold ${goal.current_progress ? 'text-green-700 dark:text-green-400' : 'text-slate-400 dark:text-slate-500 italic'}`}>
                                  {goal.current_progress || 'No progress data yet'}
                                </span>
                              </div>
                              <div className="flex items-start gap-1.5 text-xs">
                                <span className="text-slate-500 dark:text-slate-400 shrink-0 pt-0.5">▸ Notes:</span>
                                <input
                                  type="text"
                                  value={goal.progress_notes || ''}
                                  onChange={e => updateGoalProgressField(gi, 'progress_notes', e.target.value)}
                                  placeholder="Add progress notes..."
                                  className="flex-1 px-1.5 py-0.5 text-xs rounded border border-gray-200 dark:border-[#334155] bg-transparent dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-1 focus:ring-indigo-500"
                                />
                              </div>
                              <div className="flex items-center gap-1.5 text-xs">
                                <span className="text-slate-500 dark:text-slate-400 shrink-0">▸ Last Measured:</span>
                                <DatePicker
                                  value={goal.progress_date || ''}
                                  onChange={(v) => updateGoalProgressField(gi, 'progress_date', v || null)}
                                  className="px-1.5 py-0.5 text-xs rounded border border-gray-200 dark:border-[#334155] bg-transparent dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-1 focus:ring-indigo-500"
                                  aria-label="Last measured date"
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* c8: Services */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="services_lre" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Services</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                {pre.services && (() => {
                  const entries = Object.entries(pre.services);
                  const rows = [];
                  for (let i = 0; i < entries.length; i += 2) {
                    rows.push(entries.slice(i, i + 2));
                  }
                  return rows.map((pair, ri) => (
                    <tr key={ri}>
                      {pair.map(([k, v]) => (
                        <td key={k} colSpan={3} className={CELL}>
                          <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`pre_meeting_check.services.${k}`, val)} />
                        </td>
                      ))}
                      {pair.length < 2 && <td colSpan={3} className={CELL} />}
                    </tr>
                  ));
                })()}
                <tr>
                  <td colSpan={3} className={CELL}>Services only include subject areas?</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={pre.services_only_subject_areas} editing={editing} onChange={v => updateEditField('pre_meeting_check.services_only_subject_areas', v)} /></td>
                </tr>
                <tr>
                  <td colSpan={3} className={CELL}>Required related services addressed?</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={pre.required_related_services} editing={editing} onChange={v => updateEditField('pre_meeting_check.required_related_services', v)} /></td>
                </tr>

                {/* c9: Supplementary Aids */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="accommodations" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Supplementary Aids</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                <tr>
                  {pre.supplementary_aids && Object.entries(pre.supplementary_aids).map(([k, v]) => (
                    <td key={k} colSpan={3} className={CELL}>
                      <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`pre_meeting_check.supplementary_aids.${k}`, val)} />
                    </td>
                  ))}
                  {!pre.supplementary_aids && <td colSpan={6} className={`${CELL} text-slate-400 italic`}>No data</td>}
                </tr>

                {/* c10: Assessment Participation */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="assessment_participation" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Assessment Participation</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                <tr>
                  <td colSpan={6} className={CELL}>
                    <CBLabel label="Marked correctly" checked={pre.assessment_participation?.marked_correctly} editing={editing} onChange={v => updateEditField('pre_meeting_check.assessment_participation.marked_correctly', v)} />
                  </td>
                </tr>
                {(pre.assessment_participation?.sa_note || pre.assessment_participation?.pa_note || pre.assessment_participation?.paa_note) && (
                  <tr>
                    <td colSpan={6} className={`${CELL} text-xs text-slate-600 dark:text-slate-400`}>
                      {pre.assessment_participation.sa_note && <p className="py-0.5">{pre.assessment_participation.sa_note}</p>}
                      {pre.assessment_participation.pa_note && <p className="py-0.5">{pre.assessment_participation.pa_note}</p>}
                      {pre.assessment_participation.paa_note && <p className="py-0.5">{pre.assessment_participation.paa_note}</p>}
                    </td>
                  </tr>
                )}

                {/* c11: ESY */}
                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="iep_compliance" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">ESY (Extended School Year)</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                {pre.esy && (() => {
                  const entries = Object.entries(pre.esy);
                  const rows = [];
                  for (let i = 0; i < entries.length; i += 2) { rows.push(entries.slice(i, i + 2)); }
                  return rows.map((pair, ri) => (
                    <tr key={ri}>
                      {pair.map(([k, v]) => (
                        <td key={k} colSpan={3} className={CELL}>
                          <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`pre_meeting_check.esy.${k}`, val)} />
                        </td>
                      ))}
                      {pair.length < 2 && <td colSpan={3} className={CELL} />}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
            </div>

            {/* ─── SECTION D: AFTER THE MEETING ─── */}
            <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[480px]">
              <tbody>
                <tr><td colSpan={6} className={SECTION_TITLE}>AFTER THE MEETING</td></tr>

                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="iep_compliance" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Participants:</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>
                {post.participants && (() => {
                  const entries = Object.entries(post.participants);
                  const rows = [];
                  for (let i = 0; i < entries.length; i += 2) { rows.push(entries.slice(i, i + 2)); }
                  return rows.map((pair, ri) => (
                    <tr key={ri}>
                      {pair.map(([k, v]) => (
                        <td key={k} colSpan={3} className={CELL}>
                          <CBLabel label={formatKey(k)} checked={v} editing={editing} onChange={val => updateEditField(`post_meeting.participants.${k}`, val)} />
                        </td>
                      ))}
                      {pair.length < 2 && <td colSpan={3} className={CELL} />}
                    </tr>
                  ));
                })()}

                <tr>
                  <td colSpan={3} className={CELL}>Placement decision made?</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={post.placement_decision} editing={editing} onChange={v => updateEditField('post_meeting.placement_decision', v)} /></td>
                </tr>
                <tr>
                  <td colSpan={3} className={CELL}>Periodic progress reports?</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={post.periodic_progress_reports} editing={editing} onChange={v => updateEditField('post_meeting.periodic_progress_reports', v)} /></td>
                </tr>
                <tr>
                  <td colSpan={3} className={CELL}>Initial placement on file?</td>
                  <td colSpan={3} className={CELL}><YesNoInline obj={post.initial_placement_on_file} editing={editing} onChange={v => updateEditField('post_meeting.initial_placement_on_file', v)} /></td>
                </tr>
              </tbody>
            </table>
            </div>

            {/* ─── SECTION E: TRANSITION PLANS ─── */}
            <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[480px]">
              <tbody>
                <tr><td colSpan={6} className={SECTION_TITLE}>TRANSITION PLANS (Students 14+)</td></tr>

                <tr>
                  <td colSpan={6} className={`${CELL} bg-slate-50 dark:bg-slate-800/60`}>
                    <ChecklistSectionPopover sectionKey="iep_compliance" sectionDetails={sd} onOpenFileAtPage={handleOpenFileAtPage}>
                      <span className="font-medium">Transition Planning</span>
                    </ChecklistSectionPopover>
                  </td>
                </tr>

                {[
                  ['student_invited', 'Student invited?'],
                  ['age_appropriate_assessments', 'Age-appropriate assessments?'],
                  ['career_employment_goals', 'Career/Employment goals?'],
                  ['education_training_goals', 'Education/Training goals?'],
                  ['independent_living_goals', 'Independent living goals?'],
                  ['career_employment_services', 'Career/Employment services?'],
                  ['education_training_services', 'Education/Training services?'],
                  ['independent_living_services', 'Independent living services?'],
                  ['outside_agency_invited', 'Outside agency invited?'],
                  ['services_by_another_agency', 'Services by another agency?'],
                  ['multi_year_courses', 'Multi-year courses?'],
                  ['annual_iep_goal_related', 'Annual IEP goal related?'],
                ].map(([key, label], idx) => (
                  <tr key={key} className={idx % 2 === 0 ? 'bg-slate-50/50 dark:bg-slate-800/30' : ''}>
                    <td colSpan={3} className={CELL}>{label}</td>
                    <td colSpan={3} className={CELL}><YesNoInline obj={transition[key]} editing={editing} onChange={v => updateEditField(`transition_plans.${key}`, v)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

           </div>
          </div>
        )}

      </div>

      <DocumentViewer
        open={!!viewerFile}
        file={viewerFile}
        page={viewerPage}
        studentId={id}
        onClose={() => { setViewerFile(null); setViewerPage(null); }}
      />
    </div>
  );
}
